// Shared password validation utility
// Balanced policy: secure but not overly restrictive

export const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 8,
  MAX_LENGTH: 128,
  REQUIRE_MIXED_CASE: true, // At least one uppercase and one lowercase
  REQUIRE_NUMBERS: true, // At least one number
  REQUIRE_SPECIAL_CHARS: false, // Optional special characters for better UX
  SUGGEST_SPECIAL_CHARS: true, // Encourage but don't require
} as const;

// Regex patterns for validation
export const PASSWORD_PATTERNS = {
  LOWERCASE: /[a-z]/,
  UPPERCASE: /[A-Z]/,
  DIGIT: /[0-9]/,
  SPECIAL_CHAR: /[^a-zA-Z0-9]/, // Non-alphanumeric characters
} as const;

// Password strength scoring constants
export const PASSWORD_STRENGTH = {
  LENGTH_BONUS_SHORT: 20,
  LENGTH_BONUS_LONG: 10,
  MIN_LENGTH_FOR_BONUS: 12,
  MIN_LENGTH_FOR_EXTRA_BONUS: 16,
  CHARACTER_TYPE_BONUS: 15,
  MAX_SCORE: 100,
  WEAK_THRESHOLD: 30,
  FAIR_THRESHOLD: 50,
  GOOD_THRESHOLD: 70,
  STRONG_THRESHOLD: 90,
} as const;

/**
 * Validates password against the balanced policy requirements
 */
export function validatePassword(password: string): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Length validation
  if (password.length < PASSWORD_REQUIREMENTS.MIN_LENGTH) {
    errors.push(`Password must be at least ${PASSWORD_REQUIREMENTS.MIN_LENGTH} characters long.`);
  }

  if (password.length > PASSWORD_REQUIREMENTS.MAX_LENGTH) {
    errors.push(`Password must be less than ${PASSWORD_REQUIREMENTS.MAX_LENGTH} characters.`);
  }

  // Character type validation
  if (PASSWORD_REQUIREMENTS.REQUIRE_MIXED_CASE) {
    if (!PASSWORD_PATTERNS.LOWERCASE.test(password)) {
      errors.push('Password must contain at least one lowercase letter.');
    }
    if (!PASSWORD_PATTERNS.UPPERCASE.test(password)) {
      errors.push('Password must contain at least one uppercase letter.');
    }
  }

  if (PASSWORD_REQUIREMENTS.REQUIRE_NUMBERS && !PASSWORD_PATTERNS.DIGIT.test(password)) {
    errors.push('Password must contain at least one number.');
  }

  // Special characters - suggested but not required
  if (
    PASSWORD_REQUIREMENTS.SUGGEST_SPECIAL_CHARS &&
    !PASSWORD_PATTERNS.SPECIAL_CHAR.test(password)
  ) {
    warnings.push('Consider adding special characters for stronger security.');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Calculates password strength score (0-100)
 */
export function calculatePasswordStrength(password: string): number {
  let strength = 0;

  // Length bonuses
  if (password.length >= PASSWORD_REQUIREMENTS.MIN_LENGTH) {
    strength += PASSWORD_STRENGTH.LENGTH_BONUS_SHORT;
  }
  if (password.length >= PASSWORD_STRENGTH.MIN_LENGTH_FOR_BONUS) {
    strength += PASSWORD_STRENGTH.LENGTH_BONUS_LONG;
  }

  // Character variety bonuses
  if (PASSWORD_PATTERNS.LOWERCASE.test(password)) {
    strength += PASSWORD_STRENGTH.CHARACTER_TYPE_BONUS;
  }
  if (PASSWORD_PATTERNS.UPPERCASE.test(password)) {
    strength += PASSWORD_STRENGTH.CHARACTER_TYPE_BONUS;
  }
  if (PASSWORD_PATTERNS.DIGIT.test(password)) {
    strength += PASSWORD_STRENGTH.CHARACTER_TYPE_BONUS;
  }
  if (PASSWORD_PATTERNS.SPECIAL_CHAR.test(password)) {
    strength += PASSWORD_STRENGTH.CHARACTER_TYPE_BONUS;
  }

  // Extra length bonus
  if (password.length >= PASSWORD_STRENGTH.MIN_LENGTH_FOR_EXTRA_BONUS) {
    strength += PASSWORD_STRENGTH.LENGTH_BONUS_LONG;
  }

  return Math.min(strength, PASSWORD_STRENGTH.MAX_SCORE);
}

/**
 * Gets password strength label and color based on score
 */
export function getPasswordStrengthLabel(score: number): {
  label: string;
  color: string;
} {
  if (score < PASSWORD_STRENGTH.WEAK_THRESHOLD) {
    return { label: 'Weak', color: 'text-red-400' };
  }
  if (score < PASSWORD_STRENGTH.FAIR_THRESHOLD) {
    return { label: 'Fair', color: 'text-orange-400' };
  }
  if (score < PASSWORD_STRENGTH.GOOD_THRESHOLD) {
    return { label: 'Good', color: 'text-yellow-400' };
  }
  if (score < PASSWORD_STRENGTH.STRONG_THRESHOLD) {
    return { label: 'Strong', color: 'text-green-400' };
  }
  return { label: 'Very Strong', color: 'text-emerald-400' };
}

/**
 * Gets password strength progress bar color based on score
 */
export function getPasswordStrengthColor(score: number): string {
  if (score < PASSWORD_STRENGTH.WEAK_THRESHOLD) {
    return 'bg-red-500';
  }
  if (score < PASSWORD_STRENGTH.FAIR_THRESHOLD) {
    return 'bg-orange-500';
  }
  if (score < PASSWORD_STRENGTH.GOOD_THRESHOLD) {
    return 'bg-yellow-500';
  }
  if (score < PASSWORD_STRENGTH.STRONG_THRESHOLD) {
    return 'bg-green-500';
  }
  return 'bg-emerald-500';
}

/**
 * Zod schema for password validation
 */
import { z } from 'zod';

export const passwordSchema = z
  .string()
  .min(
    PASSWORD_REQUIREMENTS.MIN_LENGTH,
    `Password must be at least ${PASSWORD_REQUIREMENTS.MIN_LENGTH} characters long.`
  )
  .max(
    PASSWORD_REQUIREMENTS.MAX_LENGTH,
    `Password must be less than ${PASSWORD_REQUIREMENTS.MAX_LENGTH} characters.`
  )
  .regex(PASSWORD_PATTERNS.LOWERCASE, 'Password must contain at least one lowercase letter.')
  .regex(PASSWORD_PATTERNS.UPPERCASE, 'Password must contain at least one uppercase letter.')
  .regex(PASSWORD_PATTERNS.DIGIT, 'Password must contain at least one number.');
// Note: Special characters are suggested but not required
